import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, UtensilsCrossed, X } from 'lucide-react';
import { supabase } from '../../utils/supabase';

const categories = ['All', 'Grills', 'Fast Food', 'Sides', 'Drinks', 'Desserts'];

const categoryEmoji = {
  Grills: '🔥',
  'Fast Food': '🍔',
  Sides: '🥗',
  Drinks: '🥤',
  Desserts: '🍰',
};

export default function Menu() {
  const [items, setItems] = useState([]);
  const [restaurantId, setRestaurantId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({
    name: '', name_ar: '', category: 'Grills', price: '', description: ''
  });

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: rest } = await supabase
        .from('restaurants')
        .select('id')
        .eq('email', user.email)
        .single();

      if (!rest) return;
      setRestaurantId(rest.id);

      const { data: menuData } = await supabase
        .from('menu_items')
        .select('*')
        .eq('restaurant_id', rest.id)
        .order('created_at', { ascending: true });

      setItems(menuData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(item => {
    const matchCat = activeCategory === 'All' || item.category === activeCategory;
    const matchSearch =
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      (item.name_ar || '').includes(search);
    return matchCat && matchSearch;
  });

  const availableCount = items.filter(i => i.available).length;

  const toggleAvailable = async (id, current) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, available: !current } : i));
    const { error } = await supabase
      .from('menu_items')
      .update({ available: !current })
      .eq('id', id);
    if (error) {
      setItems(prev => prev.map(i => i.id === id ? { ...i, available: current } : i));
    }
  };

  const deleteItem = async (id) => {
    setItems(prev => prev.filter(i => i.id !== id));
    setDeleteConfirm(null);
    const { error } = await supabase
      .from('menu_items')
      .delete()
      .eq('id', id);
    if (error) {
      console.error(error);
      fetchMenu();
    }
  };

  const openAdd = () => {
    setEditItem(null);
    setForm({ name: '', name_ar: '', category: 'Grills', price: '', description: '' });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name,
      name_ar: item.name_ar || '',
      category: item.category || 'Grills',
      price: item.price,
      description: item.description || '',
    });
    setShowModal(true);
  };

  const saveItem = async () => {
    if (!form.name.trim() || !form.price) return;
    setSaving(true);

    try {
      if (editItem) {
        const { data, error } = await supabase
          .from('menu_items')
          .update({
            name: form.name.trim(),
            name_ar: form.name_ar.trim(),
            category: form.category,
            price: Number(form.price),
            description: form.description.trim(),
          })
          .eq('id', editItem.id)
          .select()
          .single();

        if (error) throw error;
        setItems(prev => prev.map(i => i.id === editItem.id ? data : i));
      } else {
        const { data, error } = await supabase
          .from('menu_items')
          .insert({
            restaurant_id: restaurantId,
            name: form.name.trim(),
            name_ar: form.name_ar.trim(),
            category: form.category,
            price: Number(form.price),
            description: form.description.trim(),
            available: true,
          })
          .select()
          .single();

        if (error) throw error;
        setItems(prev => [...prev, data]);
      }
      setShowModal(false);
    } catch (err) {
      console.error(err);
      alert('Failed to save item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="h-7 bg-gray-100 rounded w-32 animate-pulse" />
          <div className="h-9 bg-gray-100 rounded-xl w-24 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse h-40" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Menu Manager</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {items.length} items · {availableCount} available
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-secondary transition shadow-sm"
        >
          <Plus size={16} /> Add Item
        </button>
      </div>

      {/* Search + Categories */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search menu..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {categories.map(cat => {
            const count = cat === 'All' ? items.length : items.filter(i => i.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition flex items-center gap-1.5
                  ${activeCategory === cat ? 'bg-primary text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
              >
                {cat !== 'All' && <span>{categoryEmoji[cat]}</span>}
                {cat}
                {count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCategory === cat ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Empty State */}
      {filtered.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <UtensilsCrossed size={28} className="text-gray-200" />
          </div>
          <p className="text-gray-400 font-medium">
            {items.length === 0 ? 'No menu items yet' : 'No items found'}
          </p>
          <p className="text-gray-300 text-sm mt-1">
            {items.length === 0 ? 'Add your first item — it will appear in your WhatsApp bot automatically!' : 'Try a different category or search'}
          </p>
          {items.length === 0 && (
            <button
              onClick={openAdd}
              className="mt-4 bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-secondary transition"
            >
              + Add First Item
            </button>
          )}
        </div>
      )}

      {/* Menu Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(item => (
          <div
            key={item.id}
            className={`bg-white rounded-2xl border shadow-sm p-5 transition hover:shadow-md ${!item.available ? 'opacity-60 border-gray-100' : 'border-gray-100'}`}
          >
            {/* Category badge */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0 mr-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-lg">
                    {categoryEmoji[item.category]} {item.category}
                  </span>
                </div>
                <h3 className="font-bold text-gray-800 truncate">{item.name}</h3>
                {item.name_ar && (
                  <p className="text-sm text-gray-400" dir="rtl">{item.name_ar}</p>
                )}
              </div>
              <span className="bg-primary/10 text-primary font-bold text-sm px-3 py-1.5 rounded-xl shrink-0">
                SAR {item.price}
              </span>
            </div>

            {item.description && (
              <p className="text-xs text-gray-500 mb-4 line-clamp-2 leading-relaxed">{item.description}</p>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-gray-50 mt-auto">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleAvailable(item.id, item.available)}
                  className={`relative inline-flex shrink-0 w-11 h-6 rounded-full transition-colors ${item.available ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${item.available ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className={`text-xs font-medium ${item.available ? 'text-green-600' : 'text-gray-400'}`}>
                  {item.available ? 'Available' : 'Unavailable'}
                </span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => openEdit(item)}
                  className="p-2 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-500 transition"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => setDeleteConfirm(item.id)}
                  className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-400 transition"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Trash2 size={20} className="text-red-400" />
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">Delete Item?</h3>
            <p className="text-gray-400 text-sm text-center mb-6">This item will be removed from your menu and WhatsApp bot.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteItem(deleteConfirm)}
                className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800">
                {editItem ? '✏️ Edit Item' : '➕ Add New Item'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Name (English) *</label>
                  <input
                    placeholder="e.g. Shawarma"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">الاسم بالعربي</label>
                  <input
                    placeholder="شاورما"
                    value={form.name_ar}
                    onChange={e => setForm({ ...form, name_ar: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-right"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Category</label>
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {categories.filter(c => c !== 'All').map(c => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Price (SAR) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">SAR</span>
                    <input
                      placeholder="0"
                      type="number"
                      value={form.price}
                      onChange={e => setForm({ ...form, price: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl pl-12 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                  Description <span className="text-gray-300 font-normal">(optional)</span>
                </label>
                <textarea
                  placeholder="Short description of the item..."
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={saveItem}
                disabled={saving || !form.name.trim() || !form.price}
                className="flex-1 bg-primary text-white py-3 rounded-xl text-sm font-medium hover:bg-secondary transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Saving...
                  </>
                ) : editItem ? 'Save Changes' : '+ Add Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}